/**
 * Category hub pages: title/description/intro copy and FAQs for each of the 7 hubs.
 * Tool membership is NOT stored here — it derives from src/data/tools.js.
 *
 * Intro copy is user-facing. Write it for a visitor deciding which tool to open,
 * never about search strategy.
 */

export const CATEGORY_HUBS = [
  {
    id: 'developer',
    route: 'developer-tools',
    navTitle: 'Developer tools',
    title: 'Free Online Developer Tools — JSON, UUID, JWT, Base64, Hash & More | WebToolz365',
    description:
      'Browse free in-browser tools: JSON formatters, regex testing, cron expression builder, SQL formatter, CSV/JSON conversion, UUIDs, Base64, hashes, QR codes, JWT decode, and Markdown preview. No signup; processing stays local where possible.',
    keywords:
      'free online tools, json formatter online, regex tester online, cron expression builder, sql formatter online, csv to json converter, uuid generator free, jwt decoder online, base64 encoder, hash generator, client-side tools',
    h1: 'Free online developer tools',
    introParagraphs: [
      'Developers search for specific jobs: pretty-print JSON before a PR, decode a JWT from a staging cookie, bulk-generate UUIDs for fixtures, or turn a Python dict into strict JSON for an API doc. This hub groups those utilities in one place so you can bookmark one index and deep-link to the exact tool when you need it.',
      'Most of these handle the awkward cases other formatters reject. The JSON formatter accepts trailing commas, comments, and single-quoted keys instead of failing on the first error. The Python dict converter understands True, False, None, and single quotes. The JWT decoder shows header and payload claims without asking you to paste a token into a server you do not control.',
      'Every listing below runs primarily in your browser on WebToolz365: no accounts, no paywall on core features, and no server round-trip for the sensitive paths. Use the JSON formatter for RFC-style validation, the Python dict converter when samples use single quotes and True/False/None, and the JWT decoder when you only need claims—not another npm install.',
      'Each tool page links back to the others it pairs with, so you can chain steps without returning here: format a JSON payload, diff it against yesterday\u2019s, then hash the result. Bookmark this page as your index, or bookmark the individual tool you reach for most.',
    ],
    faqItems: [
      {
        question: 'Are these developer tools free?',
        acceptedAnswer:
          'Yes. WebToolz365 developer tools are free to use. Core processing runs in your browser for privacy; no payment is required to use the features described on each tool page.',
      },
      {
        question: 'Do I need an account?',
        acceptedAnswer:
          'No account is required. Open a tool, paste or upload where supported, and copy the result.',
      },
      {
        question: 'Which tool should I use for JSON cleanup?',
        acceptedAnswer:
          'Use the JSON formatter & beautifier for indent/minify/validate. If your snippet is Python-style (single quotes, True/False/None), use the Python dict to JSON converter instead.',
      },
    ],
  },
  {
    id: 'pdf',
    route: 'pdf-tools',
    navTitle: 'PDF tools',
    title: 'Free PDF Tools Online — Merge, Split & Extract Text | WebToolz365',
    description:
      'Merge PDFs into one file, split a document by page range, extract plain text, or convert to an editable Word document. Each task has its own page with its own options.',
    keywords:
      'pdf to word converter, pdf to word, convert pdf to word, pdf merger online free, split pdf online, pdf to text extractor, merge pdf files, pdf splitter, extract text from pdf, client-side pdf tools',
    h1: 'Free PDF tools online',
    introParagraphs: [
      'PDF tasks are extremely common in support, legal, and engineering workflows: merge scanned attachments, split a contract by chapter, or pull plain text from a report. Many users explicitly search for no-upload or offline-capable solutions because documents are confidential.',
      'Each tool does one job and states its options plainly: the merger lets you set the order before combining, the splitter takes explicit page ranges, and the text extractor returns selectable text rather than an image of the page.',
      'Every tool page says exactly what happens to your file and where the processing runs, along with the real limits — very large files are bounded by your device\u2019s memory, and scanned PDFs with no text layer need OCR, which these tools do not perform.',
    ],
    faqItems: [
      {
        question: 'Are PDFs uploaded to your servers?',
        acceptedAnswer:
          'These tools are built to process files in your browser where supported. Check each tool page for the exact privacy note and browser requirements.',
      },
      {
        question: 'Which tool should I use to combine PDFs?',
        acceptedAnswer:
          'Use the PDF merger to combine multiple files into one document in the order you choose.',
      },
    ],
  },
  {
    id: 'image',
    route: 'image-tools',
    navTitle: 'Image tools',
    title: 'Free Online Image Tools — Convert, Resize & Compress | WebToolz365',
    description:
      'Convert between JPG, PNG, WebP, AVIF, HEIC; resize by pixels or percent; compress with a quality slider—all with clear, tool-specific pages for image SEO queries.',
    keywords:
      'image converter online free, png to jpg, webp converter, image resizer online, compress image without losing quality, heic to jpg',
    h1: 'Free image converter, resizer & compressor',
    introParagraphs: [
      'Different jobs need different tools: WebP or AVIF to cut page weight, HEIC to JPG so an iPhone photo opens on Windows, or a straight resize to fit an upload limit. Each has its own page so the options you see are only the ones that apply.',
      'The flow is the same everywhere: choose your output, check the dimensions and file size before you commit, then download. Conversion happens on your device, so nothing is uploaded and there is no queue to wait in.',
    ],
    faqItems: [
      {
        question: 'Do image tools work on mobile?',
        acceptedAnswer:
          'Yes in modern mobile browsers that support the required APIs. Very large images may be slower on low-end devices.',
      },
    ],
  },
  {
    id: 'text',
    route: 'text-tools',
    navTitle: 'Text tools',
    title: 'Free Text & Diff Tools Online — Compare, Count, Case & Lorem | WebToolz365',
    description:
      'Compare text or JSON, run a diff, count words, change case, or generate lorem ipsum. Targets writers, QA, and developers cleaning up copy or configs.',
    keywords:
      'text diff online, compare two texts, json diff online, word counter, case converter online, lorem ipsum generator',
    h1: 'Free text, diff & writing utilities',
    introParagraphs: [
      'These cover the everyday editing jobs that are annoying without a proper tool: spotting what changed between two versions of a document, diffing JSON structurally rather than line by line, checking a word count against a hard limit, or converting case across a block of text.',
      'They pair naturally with the developer tools — format a JSON file first, then compare it against another — and everything runs in your browser, so unpublished drafts and config files never leave your device.',
    ],
    faqItems: [
      {
        question: 'Is text comparison private?',
        acceptedAnswer:
          'Comparison runs in your browser for the tools designed to be client-side. See each tool page for details.',
      },
    ],
  },
  {
    id: 'utility',
    route: 'utility-tools',
    navTitle: 'Utility tools',
    title: 'Free Online Utilities — Currency, Color, Timestamp, URL, IP & DNS | WebToolz365',
    description:
      'Currency converter, color picker, Unix timestamp converter, world clock, timezone meeting planner, unit converter, URL encode/decode, public IP lookup, IP geolocation, IP-from-URL DNS resolution, percentage math, and random integers in the browser.',
    keywords:
      'currency converter online, live exchange rates, unit converter online, timezone meeting planner, unix timestamp converter online, world clock online, url encoder decoder, what is my ip, color picker hex, online utilities',
    h1: 'Free browser utilities',
    introParagraphs: [
      'Utility tools answer immediate questions: “what is my IP”, “encode this query string”, “convert ms to date”. Separate URLs with explicit H1s and supporting copy help search engines map those questions to your pages.',
      'The network tools tell you how they work rather than treating it as a black box: domain lookups resolve over DNS-over-HTTPS from your browser, and each page notes when a request has to reach an external service so you always know what left your device.'
    ],
    faqItems: [
      {
        question: 'Do utilities require JavaScript?',
        acceptedAnswer:
          'Yes. These tools run as interactive web apps in your browser. Disable script blockers for the site if a tool does not load.',
      },
    ],
  },
  {
    id: 'math',
    route: 'math-tools',
    navTitle: 'Math tools',
    title: 'Free Online Math Calculators — BMI, EMI, Age, Tip & More | WebToolz365',
    description: 'Free browser math calculators: BMI, age, tip/bill splitter, loan EMI, compound interest, discount, scientific calculator, mean/median/mode, area/volume, base converter, fractions, GCD, LCM, prime checker, quadratic solver. No signup.',
    keywords: 'bmi calculator, age calculator, tip calculator, loan emi calculator, compound interest calculator, discount calculator, scientific calculator online, mean median mode calculator, area calculator, number base converter, fraction calculator, gcd lcm calculator, prime number checker, quadratic equation solver',
    h1: 'Free online math calculators',
    introParagraphs: [
      'Daily math tasks — splitting a restaurant bill, checking if a home loan EMI fits your budget, computing the exact age for a visa application — rarely need a spreadsheet. These dedicated calculators give you instant answers with clear inputs and labeled results.',
      'Each calculator runs entirely in your browser. No account, no upload, no server round-trip. Type a value and see the result immediately, or press Calculate for multi-step tools like the loan amortization breakdown.',
      'From everyday tasks like discount price at checkout and splitting a tip, to school math like quadratic roots with complex number output, prime factorization, and fraction simplification — this hub covers calculations that come up repeatedly in daily life, work, and study.',
      'Related tools are linked from each page so you can move between calculators without returning to search. All tools are mobile-friendly and work in both light and dark mode.',
    ],
    faqItems: [
      { question: 'Are these math calculators free?', acceptedAnswer: 'Yes. All math tools on WebToolz365 are completely free with no signup required.' },
      { question: 'Do calculations happen on a server?', acceptedAnswer: 'No. All computations run in JavaScript in your browser. No data is sent to our servers.' },
      { question: 'Which tool should I use for a home loan?', acceptedAnswer: 'Use the Loan EMI Calculator for monthly installment, total interest, and an amortization schedule. Use the Compound Interest Calculator to compare savings growth.' },
      { question: 'Does the scientific calculator support degrees and radians?', acceptedAnswer: 'Yes. The scientific calculator has a DEG/RAD toggle that converts inputs before applying trigonometric functions.' },
    ],
  },
  {
    id: 'science',
    route: 'science-tools',
    navTitle: 'Science tools',
    title: 'Free Online Science Tools — Periodic Table, Physics & Chemistry | WebToolz365',
    description: "Free browser science tools: interactive periodic table, molar mass calculator, physics formulas, Ohm's law, gas laws, half-life, wave calculator, density, resistor color code, and pH calculator. No signup.",
    keywords: "periodic table interactive, molar mass calculator, physics formula calculator, ohm's law calculator, gas laws calculator, half life calculator, wavelength frequency calculator, density calculator, resistor color code, ph calculator",
    h1: 'Free online science tools',
    introParagraphs: [
      "Science calculations appear constantly in coursework, labs, and engineering work. Checking the molar mass of a compound, reading resistor color bands, solving kinematic equations, or finding the pH of a solution — these dedicated tools give instant answers with labeled inputs and clear units.",
      "Every tool runs entirely in your browser with no signup required. No data is sent to any server. Type a value and the result updates immediately.",
      "From high school chemistry (periodic table, pH scale, gas laws) to undergraduate physics (kinematics, wave mechanics, radioactive decay) and electronics engineering (Ohm's law, resistor color codes), this hub covers the science calculations that come up most often in study and work.",
    ],
    faqItems: [
      { question: "Are these science tools free?", acceptedAnswer: "Yes. All science tools on WebToolz365 are completely free with no signup required." },
      { question: "Do the calculations happen on a server?", acceptedAnswer: "No. All calculations run in JavaScript in your browser. No data is sent to our servers." },
      { question: "Does the periodic table include all 118 elements?", acceptedAnswer: "Yes. The interactive periodic table includes all 118 confirmed elements with atomic number, symbol, name, atomic mass, category, and electron configuration." },
      { question: "Can the molar mass calculator handle complex formulas?", acceptedAnswer: "Yes. The molar mass calculator parses nested parentheses — for example Ca(OH)2, Fe2(SO4)3, and Al2(SO4)3 are all handled correctly." },
    ],
  },
]

export function getCategoryHubByRoute(route) {
  return CATEGORY_HUBS.find((c) => c.route === route) || null
}

/** Breadcrumb + in-content links: toolCategory string → hub path */
